import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { HasPermission } from "Core/security";

//Language
import { useIntl } from "react-intl";

//Actions
import { useSelector, useDispatch } from "react-redux";
import { manageSearchVehicleStatuses } from "./redux/slice";
import { openModalCreateEditManageVehicleStatus } from "./modals/createEditManageVehicleStatus/redux/slice";
import { savePreference } from "Redux/actions";

//Components
import { NotificationManager } from "Components/Notifications";
import { Grid, Typography } from "@mui/material";
import Preferences from "Core/containers/Preferences";

//Controls
import {
  Container,
  IconButton,
  MenuComponent,
  SearchInput,
  TableButtons,
  TableView,
  useTablePagination
} from "@gpstrackit/gtc-react-controls";

//Modals
import CreateEditManageVehicleStatus from "./modals/createEditManageVehicleStatus";
import DeleteManageVehicleStatus from "./modals/deleteManageVehicleStatus";
import { openModalDeleteManageVehicleStatus } from "./modals/deleteManageVehicleStatus/redux/slice";

//Helpers
import { completeColumns } from "Core/data/Helpers";
import { disableItemSelection } from "../../.globals/utils";
const KEY_PREFERENCE = "Manage-VehicleStatusTab-list";
const INITIAL_SORT = { key: "name", order: "asc" };
const ICONS_PATH = "/assets/icons/";

const VehicleStatusTab = (props) => {
  //States
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [preferences, setPreferences] = useState({});
  const [loadPreferences, setLoadPreferences] = useState(true);
  const [loadColumns, setLoadColumns] = useState(false);

  //Ñanguage
  const { messages, locale } = useIntl();

  //Redux
  const authenticatedId = useSelector((state) => state.securityRedux.authenticatedId);
  const dispatch = useDispatch();
  const openCreateEditManageVehicleStatus = useSelector(
    (state) => state.manageCreateEditManageVehicleStatusRedux.open
  );
  const openDeleteManageVehicleStatus = useSelector(
    (state) => state.manageDeleteManageVehicleStatusRedux.open
  );
  const {
    loading,
    vehicleStatusItems,
    total,
    deleteResponse: response
  } = useSelector((state) => state.manageVehicleStatusTabRedux);

  // Permissions
  const user = useSelector((state) => state.securityRedux.user);
  const editablePermissions = HasPermission(user, "", [338]);

  //Memos
  const currentColumns = useMemo(() => [
    {
      key: "name",
      title: messages["manage_vehicleStatus_statusLabel"],
      show: true,
      export: true,
      sort: true,
      render: (row) => {
        return (
          <Grid
            container
            alignItems="center"
            justifyContent="flex-start"
            className="context-menu-container"
            wrap="nowrap"
          >
            <Grid item zeroMinWidth>
              <Typography noWrap>{row?.name}</Typography>
            </Grid>
            <Grid item>
              {editablePermissions &&
                <MenuComponent
                  disabled={loading}
                  options={[messages["edit"], messages["delete"]]}
                  icon={"more_vert"}
                  className="context-menu"
                  onClick={(type, vehicleStatus) => {
                  if (type === messages["edit"]) {
                    dispatch(openModalCreateEditManageVehicleStatus(vehicleStatus));
                    return;
                  }
                  if (type === messages["delete"]) {
                    if (vehicleStatus.id)
                      dispatch(
                        openModalDeleteManageVehicleStatus({
                          deleteVehicleStatusList: [vehicleStatus]
                        })
                      );
                    return;
                  }
                }}
                  value={row}
                />
              }
            </Grid>
          </Grid>
        );
      },
      exportValue: (row) => {
        return row?.name;
      }
    },
    {
      key: "icon",
      title: messages["manage_vehicleStatus_vehicleStatusIcon"],
      show: true,
      export: true,
      render: (row) => {
        return (
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            className="context-menu-container"
            wrap="nowrap"
          >
            <img src={`${ICONS_PATH}${row?.icon}`} />
          </Grid>
        );
      },
      exportValue: (row) => {
        return row?.className;
      }
    }
  ], [locale]);

  //Table refs
  const tableRef = useRef();

  //Hooks
  const pagination = useTablePagination({
    dispatch,
    tableRef,
    exportName: "vehicle_status",
    isLocalData: true
  });

  //Helpers
  const handleDeleteVehicleStatusList = useCallback(() => {
    dispatch(openModalDeleteManageVehicleStatus({ deleteVehicleStatusList: selectedItems }));
  }, [selectedItems]);

  const refreshTable = () => {
    setSearchText("");
    dispatch(manageSearchVehicleStatuses({}));
  };

  const savePreferences = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected an array of preferences");
        return;
      }

      let updatedPreferences = { ...preferences } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferences(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: KEY_PREFERENCE,
          preferenceValue: updatedPreferences
        })
      );
    },
    [preferences]
  );

  //Effects
  useEffect(() => {
    if (preferences?.columns?.length && !loadColumns) {
      const preferenceMap = new Map(preferences.columns.map((col) => [col.key, col]));

      // Reorder columns based on preferences (keeping `show`)
      const reorderedColumns = preferences.columns
        .map((pref) => {
          const col = currentColumns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);

      // Add new columns (not listed in preferences), with `show: true` by default
      const newColumns = currentColumns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));

      const finalColumns = [...reorderedColumns, ...newColumns];

      pagination.setColumns(finalColumns);
      setPreferences({ ...preferences });
      setLoadColumns(true);
    }
  }, [preferences.columns, loadColumns]);

  useEffect(() => {
    if (pagination.columns.length > 0) {
      setPreferences({ ...preferences, columns: pagination.columns });
      setLoadColumns(false);
    }
  }, [locale]);

  useEffect(() => {
    setSelectedItems([])
    tableRef?.current?.setSelectedItems([])
    pagination.onChangeData(vehicleStatusItems);
  }, [vehicleStatusItems]);

  useEffect(() => {
    dispatch(manageSearchVehicleStatuses({}));
  }, []);

  useEffect(() => {
    pagination.setColumns(currentColumns);
  }, [locale]);

  useEffect(() => {
    if (response) {
      if (!response.error) {
        NotificationManager.success(
          messages["manage_vehicleStatus_deleted"],
          messages["success"],
          3000,
          null,
          null,
          "success"
        );
        setSearchText("");
      }
    }
  }, [response]);

  return (
    <>
      <Container maxWidth={false}>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item xs={6} sm={2}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <Grid item style={{ display: "flex" }}>
                {editablePermissions &&
                  <IconButton
                    tooltip={messages["add"]}
                    placement="bottom"
                    variant="secondary"
                    icon="add_box"
                    size="small"
                    disabled={loading}
                    onClick={() => dispatch(openModalCreateEditManageVehicleStatus())}
                  />
                }
                {editablePermissions &&
                  <IconButton
                    tooltip={messages["delete"]}
                    placement="bottom"
                    variant="secondary"
                    icon="delete"
                    size="small"
                    onClick={() => handleDeleteVehicleStatusList()}
                    style={{ opacity: !selectedItems.length && "0.6" }}
                    isDisabled={!selectedItems.length}
                  />
                }
                <TableButtons
                  messages={messages}
                  buttons={["export", "orderColumn"]}
                  {...pagination}
                  onChangeColumns={(columns) => {
                    savePreferences([{ key: "columns", value: columns }]);
                    pagination.setColumns(columns);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={4} className="mb-10 text-right">
            <SearchInput
              placeholder={messages["manage_vehicleStatus_search_by_status_label"]}
              searchText={searchText}
              onSearchText={(value) => {
                setSearchText(value);
                if (window.typingTime) {
                  clearTimeout(window.typingTime);
                }
                window.typingTime = setTimeout(() => {
                  tableRef && tableRef.current?.setPage(1);
                  dispatch(manageSearchVehicleStatuses({ openSearch: value }));
                }, 1000);
              }}
            />
          </Grid>
        </Grid>
        <Preferences
          preferencesKey={KEY_PREFERENCE}
          onInitialPreferences={(data) => {
            if (!loadPreferences) return;
            setTimeout(() => {
              let sort = data?.sort || INITIAL_SORT; // * Initial sort
              tableRef.current?.setSort?.(sort);
              pagination.onChangeSort(sort);
            }, 100);
            setPreferences(data);
            setLoadPreferences(false);
          }}
        >
          <TableView
            keyColumn="id"
            loading={loading || loadPreferences}
            topheight={235}
            data={vehicleStatusItems}
            disableItemSelection={disableItemSelection(pagination)}
            total={total}
            exportOrientation="landscape"
            onSelectedItems={(items) => setSelectedItems(items)}
            {...pagination}
            onChangeColumns={(columns) => {
              const newcurrentColumns = pagination?.columns || [];
              const finalsColumns = completeColumns(newcurrentColumns, columns);
              savePreferences([{ key: "columns", value: finalsColumns }]);
              pagination.onChangeColumns(finalsColumns);
            }}
            onChangeSort={(newSort) => {
              savePreferences([{ key: "sort", value: newSort }]);
              pagination.onChangeSort(newSort);
            }}
          />
        </Preferences>
      </Container>
      {openCreateEditManageVehicleStatus && (
        <CreateEditManageVehicleStatus refresh={refreshTable} />
      )}
      {openDeleteManageVehicleStatus && <DeleteManageVehicleStatus refresh={refreshTable} />}
    </>
  );
};

export default VehicleStatusTab;
